import i18next from 'i18next'
import React from 'react'
import {
  AsyncState,
  IOWidget,
  Fun,
  fromJSX,
  onlyIf,
  nothing,
  Widget,
  any,
  async,
} from 'widgets-for-react'
import { generateBreadcrumbs } from '../../../../utils/breadcrumbs'
import { DataLayer } from '../../../../utils/datalayer'
import { ModalLayout } from '../../../../utils/modal'
import { componentBannerWidget } from '../../banner/bannerWidget'
import { CollectionsLoaderState } from '../../collections/collectionsLoader/collectionsLoaderState'
import { isSaved } from '../../collections/collectionsLoader/collectionsLoaderUtils'
import { paywallWidget } from '../../paywall/paywallWidget'
import { RootLayout } from '../../rootLayout'
import { routeUpdaters } from '../../routes/routesState'
import { UserSubscriptions } from '../../../../subscription/subscriptionState'
import { componentSaverWidget } from '../componentSaver/componentSaverWidget'
import { componentTeasersWidget } from '../componentTeasers/componentTeasersWidget'
import { ComponentDetailLayout } from './componentDetailLayout'
import {
  AmountConverterState,
  ComponentDetailState,
  componentDetailStateUpdaters,
  ComponentState,
} from './componentDetailState'

export const componentDetailWidget =
  (
    isLoggedIn: boolean,
    userSubscriptionsState: AsyncState<UserSubscriptions>,
    collectionsState: CollectionsLoaderState
  ): IOWidget<
    ComponentDetailState,
    Fun<
      ComponentDetailState,
      ComponentDetailState & { reloadCollections?: boolean }
    >
  > =>
  (state) => {
    const componentStateState = state.componentStateState
    return any([
      async(componentStateState).map(
        componentDetailStateUpdaters.componentStateStateUpdater
      ),
      componentStateState.kind === 'loaded'
        ? async(componentStateState.value.parentComponentNameState)
            .map(componentDetailStateUpdaters.parentComponentNameStateUpdater)
            .map(componentDetailStateUpdaters.componentStateUpdater)
        : nothing(),
      componentStateState.kind === 'loaded' &&
      (componentStateState.value.parentComponentNameState.kind === 'loaded' ||
        componentStateState.value.parentComponentNameState.kind === 'unloaded')
        ? paywalledComponentWidget(
            state.showToast,
            isLoggedIn,
            userSubscriptionsState,
            collectionsState,
            componentStateState.value,
            state.amountConverterState,
            state
          )
        : componentStateState.kind === 'loading' ||
          (componentStateState.kind === 'loaded' &&
            componentStateState.value.parentComponentNameState.kind ===
              'loading')
        ? fromJSX(() => <RootLayout.Loader hasWrapper={true} showText={true} />)
        : fromJSX(() => <RootLayout.Error />),
    ])
  }

const paywalledComponentWidget = (
  showToast: boolean,
  isLoggedIn: boolean,
  userSubscriptionsState: AsyncState<UserSubscriptions>,
  collectionsState: CollectionsLoaderState,
  componentState: ComponentState,
  amountConverterState: AmountConverterState,
  state: ComponentDetailState
): Widget<
  Fun<
    ComponentDetailState,
    ComponentDetailState & { reloadCollections?: boolean }
  >
> =>
  componentState.componentData.Public
    ? paywallWidget(
        isLoggedIn,
        userSubscriptionsState,
        undefined,
        false,
        'preFooterCta'
      )(
        any([
          componentBannerWidget(
            isLoggedIn ? 'public-subscribe' : 'public-register'
          ),
          componentWidget(
            showToast,
            isLoggedIn,
            collectionsState,
            false,
            true,
            componentState,
            amountConverterState,
            state
          ),
        ]),
        componentWidget(
          showToast,
          isLoggedIn,
          collectionsState,
          false,
          false,
          componentState,
          amountConverterState,
          state
        )
      )
    : paywallWidget(
        isLoggedIn,
        userSubscriptionsState,
        componentState.componentData.SubscriptionTypes.map(
          (subscription) => subscription.Id
        )
      )(
        any([
          onlyIf(!isLoggedIn, componentBannerWidget('premium')),
          componentWidget(
            showToast,
            isLoggedIn,
            collectionsState,
            true,
            false,
            componentState,
            amountConverterState,
            state
          ),
        ]),
        componentWidget(
          showToast,
          isLoggedIn,
          collectionsState,
          false,
          false,
          componentState,
          amountConverterState,
          state
        )
      )

const componentWidget = (
  showToast: boolean,
  isLoggedIn: boolean,
  collectionsState: CollectionsLoaderState,
  hasPaywall: boolean,
  hasPrefootCta: boolean,
  componentState: ComponentState,
  amountConverterState: AmountConverterState,
  state: ComponentDetailState
): Widget<
  Fun<
    ComponentDetailState,
    ComponentDetailState & { reloadCollections?: boolean }
  >
> => {
  const {
    componentData,
    parentComponentNameState: componentNameState,
    lastViewedComponentUpdateState,
    comparableComponentsState,
    componentSaverState,
    showLoggedOutEditComponentNotification,
  } = componentState
  const {
    Id,
    Brand,
    ImageURL,
    Name,
    Introduction,
    Preparation,
    ServingSuggestions,
    ParentComponentId,
    UserId,
    Draft,
    allergens,
    ingredients,
  } = componentData
  return any([
    onlyIf(
      isLoggedIn,
      async(lastViewedComponentUpdateState)
        .map(componentDetailStateUpdaters.lastViewedComponentUpdateStateUpdater)
        .map(componentDetailStateUpdaters.componentStateUpdater)
    ),
    componentNameState.kind === 'loaded' ||
    componentNameState.kind === 'unloaded'
      ? fromJSX((setState) => (
          <ComponentDetailLayout
            showToast={showToast}
            onCloseToast={() => {
              setState(componentDetailStateUpdaters.showToastUpdater(false))
            }}
            id={Id}
            allergens={allergens}
            name={Name}
            introduction={Introduction}
            preparation={Preparation}
            servingSuggestions={ServingSuggestions}
            ingredients={ingredients
              .map((ingr) => ({
                id: ingr.id,
                amount: ingr.amount,
                ingredient: ingr.ingredient.translation,
                prepreparations: ingr.prepreparations.map((p) => p.translation),
                unitOfMeasurement: ingr.unitOfMeasurement.translation,
              }))
              .slice(0, hasPaywall ? 5 : ingredients.length)}
            brand={Brand}
            image={ImageURL}
            isOwnComponent={UserId !== ''}
            parentComponent={
              componentNameState.kind === 'loaded'
                ? { id: ParentComponentId, name: componentNameState.value }
                : undefined
            }
            hasPaywall={hasPaywall}
            isLoggedIn={isLoggedIn}
            isSaved={isSaved(collectionsState, Id)}
            onSave={() => {
              setState(
                componentDetailStateUpdaters.componentStateUpdater(
                  componentDetailStateUpdaters.startSavingUpdater(Id)
                )
              )
              DataLayer.startSaveToCollection(Name)
            }}
            onEditWhenLoggedOut={() =>
              setState(
                componentDetailStateUpdaters.componentStateUpdater(
                  componentDetailStateUpdaters.showLoggedOutEditComponentNotificationUpdater(
                    true
                  )
                )
              )
            }
            breadcrumbs={
              !(hasPaywall || hasPrefootCta) ? generateBreadcrumbs() : undefined
            }
            isDraft={Draft}
            amountConverterState={amountConverterState}
            onChangeAmountConverterMultiplier={(multiplier, inputText, inputType) => {
              setState(componentDetailStateUpdaters.amountConverterStateUpdater(multiplier, inputText))
              
              if(multiplier) {
                // We probably need to de-bounce this call later, because typing '99' in the text field will fire two calls 
                switch (inputType) {
                  case 'text':
                    DataLayer.recalculateIngredientsText(multiplier)
                    break;
                  case 'preset':
                    DataLayer.recalculateIngredientsPreset(multiplier)
                    break;
                  case 'up':
                    DataLayer.recalculateIngredientsUp(multiplier)
                    break;
                  case 'down':
                    DataLayer.recalculateIngredientsDown(multiplier)
                    break;
                  default:
                    break;
                }
              }
            }}
          />
        ))
      : nothing(),
    componentSaverWidget(
      collectionsState,
      componentSaverState.opened
        ? componentSaverState.componentId === Id
          ? Name
          : comparableComponentsState.kind === 'loaded'
          ? comparableComponentsState.value.find(
              (teaser) => teaser.Id === componentSaverState.componentId
            )?.Name
          : undefined
        : undefined
    )(componentSaverState)
      .map(componentDetailStateUpdaters.componentSaverStateUpdater)
      .map(componentDetailStateUpdaters.componentStateUpdater),
    onlyIf(
      showLoggedOutEditComponentNotification,
      fromJSX((setState) => (
        <ModalLayout
          title={i18next.t('component:modalTitleCreateAccountToEdit')}
          onHide={() =>
            setState(
              componentDetailStateUpdaters.componentStateUpdater(
                componentDetailStateUpdaters.showLoggedOutEditComponentNotificationUpdater(
                  false
                )
              )
            )
          }
          primaryAction={{
            label: i18next.t('subscription:startYourFreeTrial'),
            kind: 'link',
            to:
              routeUpdaters(i18next.language).register.url +
              '?originPath=' +
              window.location.pathname,
          }}
          secondaryAction={{
            label: i18next.t('auth:loginHeader'),
            kind: 'link',
            to:
              routeUpdaters(i18next.language).login.url +
              `?originPath=${window.location.pathname}`,
          }}
        >
          <p>{i18next.t('component:modalTextCreateAccountToEdit')}</p>
        </ModalLayout>
      ))
    ),
    !(hasPaywall || hasPrefootCta)
      ? componentTeasersWidget(
          collectionsState,
          'component-detail',
          'link-save',
          { title: i18next.t('component:comparableComponents') },
          true
        )(comparableComponentsState)
          .map(componentDetailStateUpdaters.comparableComponentsStateUpdater)
          .map(componentDetailStateUpdaters.componentStateUpdater)
      : nothing(),
  ])
}
